/**
 * Corridor geometry. Pure maths, no DOM, no imports.
 *
 * It lives apart from the component because sampling the path is the only piece
 * of this port with real arithmetic in it, and arithmetic in a .ts module is
 * readable and testable in a way that arithmetic buried in JSX is not. The one
 * React name below is React.CSSProperties in a type position, which resolves
 * through the global namespace @types/react declares. Nothing here imports
 * anything or touches the document.
 *
 * THE SHAPE, and each rule fixes one specific artefact.
 *
 * 1. Depth is authored as apparent size, geometrically. Each card is a constant
 *    ratio bigger than the one behind it, all the way out. Spacing a straight
 *    z-range evenly instead makes the near cards tear apart from each other as
 *    the projection blows up.
 * 2. The rails open hard in the first stretch and then hold (fan above 1). That
 *    opening cancels the growth back there, which is still slow, so the ribbon
 *    leaves the centre as a flat band, bends once, and only then runs out on the
 *    diagonal. Parallel rails project to a straight cone with no bend at all.
 * 3. Neither end of the loop is ever on screen. A card dies with its inner edge
 *    past 50cqw, clear of the container. And it is born ACROSS the axis:
 *    railBirth is negative, so the newest card starts on the far side and sweeps
 *    back through the centre. That plugs the throat, so the axis stays covered
 *    at every instant and a newborn lands behind cards that already cover it,
 *    which is why it needs no fade in. Birthing on its own side instead leaves a
 *    hole at dead centre that blinks open once every cycle.
 *
 * Every length is in cqw, a percentage of the container's width, so the corridor
 * keeps its proportions at any size. The defaults were fitted against a
 * reference recording's card-height and edge-position profile.
 */

export type CorridorPath = {
  /** Strength of the projection. Lower is a wider angle, more dramatic rush. */
  perspective: number;
  /** Card width in world units. */
  cardWidth: number;
  /** Card height in world units. */
  cardHeight: number;
  /** Corner radius applied to each card. */
  cardRadius: number;
  /** On-screen card height at the waist, where a card is born. */
  birthHeight: number;
  /** On-screen card height as a card leaves the frame. */
  exitHeight: number;
  /** Lateral offset at birth. Negative starts the card across the axis. */
  railBirth: number;
  /** Lateral offset once the rails have finished opening. */
  railExit: number;
  /** How front-loaded the opening is. Above 1 opens early then holds. */
  fan: number;
  /** Y-rotation at birth, in degrees. */
  turnBirth: number;
  /** Y-rotation at exit, in degrees. */
  turnExit: number;
  /** Keyframe stops used to trace the curve. Raise only if motion looks faceted. */
  stops: number;
};

/**
 * These interact. The ribbon stays solid only while consecutive cards overlap,
 * which needs exitHeight over birthHeight spread across enough cards. Raising
 * exitHeight, dropping the card count, or pulling railExit in all push toward a
 * visible tear near the frame edge.
 *
 * A module constant, not an inline object literal at the call site. The
 * component memoizes the generated keyframe text on this identity, and an
 * inline literal would change identity on every render and regenerate roughly
 * 4.5KB of CSS text each time.
 */
export const PATH: CorridorPath = {
  perspective: 30,
  cardWidth: 18,
  cardHeight: 25,
  cardRadius: 0.4,
  birthHeight: 2.6,
  exitHeight: 46,
  railBirth: -11,
  railExit: 44,
  fan: 3.3,
  turnBirth: 6,
  turnExit: 28,
  stops: 24,
};

/** Narrow geometry, for a phone. A shorter run out, so cards do not smear. */
export const PATH_LEAN: CorridorPath = {
  ...PATH,
  exitHeight: 34,
  railExit: 30,
};

/**
 * The still's own geometry, and it needs one because a frozen frame is not the
 * same problem as a moving ribbon.
 *
 * In motion the nearest card is meant to leave: it flies out past the edge, and
 * the fact that it is half off screen at its largest is what makes the corridor
 * read as a corridor. A still has no next frame, so a card at that position is
 * simply a sliver of a face pinned to the bezel. The composed frame therefore
 * pulls the run out in from 44 to 24, which lands the nearest plate whole and
 * inside the frame at every width, and it still recedes on the real path rather
 * than on a drawing of one.
 *
 * The maths that makes this a geometry change rather than a taste one: a card's
 * on screen offset is railExit times its projected scale, not railExit. At the
 * near stop the scale is above 1, so a 44 wide rail is already past 50 percent
 * of the container before the card's own width is added.
 */
export const PATH_STILL: CorridorPath = {
  ...PATH,
  railExit: 24,
};

export type CorridorDensity = 'full' | 'lean' | 'still';

export interface DensitySpec {
  /** Cards on each rail at once. More cards is a denser corridor, not a faster
   *  one: spacing is derived from this and from speed. */
  cards: number;
  /**
   * The length of one whole journey, in seconds.
   *
   * NOTHING RUNS ON IT ANY MORE, and it is kept because the arithmetic that
   * replaced it is still written in these units. Every card's animation is
   * paused for its whole life and its frame comes from a delay of
   * calc(-(i * speed) / cards - journey * speed), where journey is the reader's
   * own progress through the pinned track. Both halves of that sum have to be
   * expressed in the same unit or the phases and the travel would drift apart,
   * so this is that unit. Changing it changes nothing a reader can see; it
   * would only rescale both halves together.
   */
  speed: number;
  /** Vertical placement of the axis, as a percentage of height. */
  axis: number;
  path: CorridorPath;
}

export const DENSITY: Record<CorridorDensity, DensitySpec> = {
  // NINE PER RAIL, AND EVERY CARD IN A SIXTEEN CARD MANIFEST STILL FLIES.
  //
  // Cards are addressed by index rather than by time, so a rail walking a
  // longer manifest than it has cards silently truncates it: nine cards against
  // sixteen entries means the last seven never appear on that rail. The obvious
  // fix is to raise the count to the manifest length, and it is the wrong one.
  // Two rails at sixteen is thirty two cards, each a 3D transformed layer with
  // a rounded mask, against a published budget of twenty simultaneously
  // animating composited surfaces on the device class this product is being
  // sold into.
  //
  // The rails are already offset by half the manifest so the same card is never
  // mirrored across the axis, and that offset is what closes the gap for free:
  // the right rail flies entries 0 to 8 and the left, offset by eight, flies 8
  // to 15 and then 0, so the union of the two rails is the whole manifest.
  // Nothing truncates, eighteen layers hold the ribbon, and the spacing stays
  // the one fitted against the reference recording.
  //
  // Those eighteen layers no longer run on a clock. They are paused and their
  // frames come from the reader's scroll position, so the budget above is spent
  // only while somebody is actually scrolling and nothing at all is animating
  // when they stop. See CloudCorridor.tsx.
  full: { cards: 9, speed: 18, axis: 52, path: PATH },
  // Eight against the phone manifest of nine, at the lean geometry, and the
  // same union argument covers it: right flies 0 to 7, left is offset by four
  // and flies 4 to 8 and then 0 to 2. Comfortably inside the safe range at
  // these birth and exit heights: much below it and consecutive cards grow too
  // fast to stay overlapped near the exit, which tears a gap in the ribbon.
  lean: { cards: 8, speed: 26, axis: 52, path: PATH_LEAN },
  // The composed still: three depths per rail, never animated, so it costs no
  // frames. Not a corridor with the motion subtracted, an authored frame that
  // happens to be the same frame the corridor rests on.
  // The axis rides higher than the moving corridor's. In motion the near cards
  // are dark edges sweeping past and copy can share the band with them; in a
  // still they are two whole cards at reading size, and a heading printed
  // across one of them is not a composition. Lifting the axis clears the lower
  // third for the copy.
  still: { cards: 3, speed: 18, axis: 38, path: PATH_STILL },
};

/**
 * Where the three still plates sit along the path, near to far.
 *
 * Pulled in from the old 0.86 for the reason PATH_STILL exists: at 0.86 the
 * nearest plate is projected past the edge of the container, which is correct
 * for a card that is leaving and wrong for a card that has to be read. At 0.80
 * the nearest card arrives whole, at reading size, on both rails, which matters
 * more now than it did: two of the six plates in the composed frame are live
 * terminal panels, and a panel that is half off the bezel is a panel nobody
 * reads a word of.
 */
export const STILL_STOPS = [0.8, 0.54, 0.3] as const;

/** One point on the path, as the transform a card carries at that point. */
export function sample(dir: 1 | -1, u: number, p: CorridorPath): string {
  // Geometric in apparent size, so consecutive cards keep a constant size ratio
  // and the ribbon stays solid at both ends.
  const scale = (p.birthHeight / p.cardHeight) * Math.pow(p.exitHeight / p.birthHeight, u);
  const z = p.perspective * (1 - 1 / scale);
  const rail = p.railExit - (p.railExit - p.railBirth) * Math.pow(1 - u, p.fan);
  const turn = p.turnBirth + (p.turnExit - p.turnBirth) * u;
  return `translate3d(${(dir * rail).toFixed(2)}cqw,0,${z.toFixed(2)}cqw) rotateY(${(
    -dir * turn
  ).toFixed(2)}deg)`;
}

/**
 * Sample the path so the CSS keyframes trace the real curve rather than a
 * straight interpolation between two endpoints.
 */
export function keyframes(dir: 1 | -1, name: string, p: CorridorPath): string {
  const steps: string[] = [];
  for (let s = 0; s <= p.stops; s += 1) {
    const u = s / p.stops;
    steps.push(`${(u * 100).toFixed(2)}%{transform:${sample(dir, u, p)}}`);
  }
  return `@keyframes ${name}{${steps.join('')}}`;
}

/**
 * The box a card occupies before the path moves it. Shared by the corridor and
 * by the still so the two cannot drift apart into two different pictures.
 *
 * Every value here is a length or a percentage. Nothing in this object paints:
 * the house rule is that an inline style may carry a number, a length, a
 * duration or a keyframe name, and the moment a property paints it belongs in
 * the stylesheet where check-tokens and a reviewer can both see it.
 */
export function cardBox(p: CorridorPath, axis: number): React.CSSProperties {
  return {
    left: '50%',
    top: `${axis}%`,
    width: `${p.cardWidth}cqw`,
    height: `${p.cardHeight}cqw`,
    marginLeft: `${-p.cardWidth / 2}cqw`,
    marginTop: `${-p.cardHeight / 2}cqw`,
    borderRadius: `${p.cardRadius}cqw`,
  };
}
